import produce from 'immer'
import React, { useState, useCallback } from 'react'
import { Col, Form, Row } from 'react-bootstrap'
import { saveAs } from 'file-saver'
import DatePicker from 'react-datepicker'
import NewReportService from '../../../services/report/NewReportService'
import IterminalTableComponent from '../../../components/table/IterminalTableComponent'
import { getPageCount } from '../../../utils/tableFunctions'
import { handleNotification } from '../../../utils/utilFunctions'
import { changeLoadingRequest } from '../../../store/app/actionTypes'
import { useDispatch } from 'react-redux'
import SimilerReasoningModal from '../../../components/modals/SimilerReasoningModal'
import common from '../../../services/applicationRegistration/common'

export interface SimilarRecordsReportFilterState {
  referenceNumber: string
  ompNumber: string
  applicantName: string
  aggrievedPersonName: string
  createdFrom: number | null
  createdTo: number | null
  limit: number
  page: number
}

const defaultFilters: SimilarRecordsReportFilterState = {
  referenceNumber: '',
  ompNumber: '',
  applicantName: '',
  aggrievedPersonName: '',
  createdFrom: null,
  createdTo: null,
  limit: 100,
  page: 1,
}

const ActionsCell = ({
  row,
  onView,
}: {
  row: any
  onView: (r: any) => void
}) => (
  <button
    className="view-similarty-button"
    onClick={(e) => {
      e.stopPropagation()
      onView(row.original)
    }}
  >
    View
  </button>
)

const similarRecordsReportTableColumns = [
  { Header: 'Original Reference No', accessor: 'originalReferenceNo' },
  {
    Header: 'Similar Record Reference No',
    accessor: 'similarRecordReferenceNo',
  },
  { Header: 'OMP Number', accessor: 'ompNumber' },
  { Header: 'Similar Criteria', accessor: 'similarCriteria' },
  { Header: 'Applicant Name', accessor: 'applicantName' },
  { Header: 'Aggrieved Person Name', accessor: 'aggrievedPersonName' },
  //{ Header: 'Program Type', accessor: 'programType' },
  { Header: 'Scheme', accessor: 'scheme' },
  { Header: 'Status', accessor: 'status' },
  {
    Header: 'Created Date',
    accessor: 'createDateAndTime',
    Cell: ({ value }: any) =>
      value != null ? new Date(value * 1000).toLocaleString() : '',
  },
  { Header: 'District', accessor: 'district' },
  { Header: 'ID Number', accessor: 'idNumber' },
  //{ Header: 'Contact No', accessor: 'contactNo' },
]

const SimilarRecordsReport = () => {
  const [filters, setFilters] =
    useState<SimilarRecordsReportFilterState>(defaultFilters)
  const [state, setState] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [pageCount, setPageCount] = useState(0)
  const [dataCount, setDataCount] = useState(0)
  const [viewModal, setViewModal] = useState<{
    show: boolean
    data: {
      applicationReference: string
      programType: string
      scheme: string
      descriptions: string[]
    }
  }>({
    show: false,
    data: {
      applicationReference: '',
      programType: '',
      scheme: '',
      descriptions: [],
    },
  })
  const dispatch = useDispatch()

  const retrieveDataCountAndData = async (pageDetails?: {
    pageIndex: number
    pageSize: number
  }) => {
    const page = pageDetails ? pageDetails.pageIndex + 1 : 1
    const limit = pageDetails?.pageSize ?? filters.limit
    const { createdFrom, createdTo } = toCreatedDateRange(filters)
    const newFilter = produce(filters, (draft) => {
      draft.page = page
      draft.limit = limit
    })
    setLoading(true)
    try {
      const [countRes, listRes] = await Promise.all([
        NewReportService.getSimilarRecordsReportCount({
          ...newFilter,
          createdFrom,
          createdTo,
        }),
        NewReportService.getSimilarRecordsReportList({
          ...newFilter,
          createdFrom,
          createdTo,
        }),
      ])
      if (countRes.status === 200) {
        setDataCount(countRes.data.count)
        setPageCount(getPageCount(countRes.data.count, limit))
      }
      if (listRes.status === 200) {
        handleNotification(listRes, 'Fetch Data Successfully')
        setState(listRes.data || [])
      } else {
        setState([])
      }
    } catch (e) {
      setState([])
      setDataCount(0)
      setPageCount(0)
    } finally {
      setLoading(false)
    }
  }

  const toCreatedDateRange = (f: SimilarRecordsReportFilterState) => {
    return {
      createdFrom: f.createdFrom ?? undefined,
      createdTo: f.createdTo ?? undefined,
    }
  }

  const onSubmit = async () => {
    const newFilter = produce(filters, (draft) => {
      draft.page = 1
    })
    setFilters(newFilter)
    await retrieveDataCountAndData({ pageIndex: 0, pageSize: newFilter.limit })
  }

  const onDownload = async () => {
    dispatch(changeLoadingRequest())
    const { createdFrom, createdTo } = toCreatedDateRange(filters)
    const requestOptions: any = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
      body: JSON.stringify({
        referenceNumber: filters.referenceNumber || undefined,
        ompNumber: filters.ompNumber || undefined,
        applicantName: filters.applicantName || undefined,
        aggrievedPersonName: filters.aggrievedPersonName || undefined,
        createdFrom,
        createdTo,
        limit: 100000,
        page: 1,
      }),
    }
    fetch(
      `${process.env.baseUrl}report/application-report/similar-records-report/download`,
      requestOptions
    )
      .then((response) => response.blob())
      .then((data) => {
        saveAs(data, 'similar_records_report.xlsx')
        dispatch(changeLoadingRequest())
        handleNotification({ status: 200 }, 'Download completed')
      })
      .catch((err) => {
        dispatch(changeLoadingRequest())
        handleNotification({ status: 400 }, 'Download failed')
        console.error('Download similar records report failed', err)
      })
  }

  const handleView = useCallback(async (row: any) => {
    if (!row.similarRecordResultId) return
    try {
      const response = await common.apiGetSimilarRecordSimilertyDetails(
        row.similarRecordResultId
      )
      const data = response.data
      const descriptions = data?.descriptions?.map((d: string) =>
        d.replace(/^Similar records found with\s*/, '').replace(/!$/, '')
      ) || ['No similar criteria details.']
      setViewModal({
        show: true,
        data: {
          applicationReference:
            data?.applicationReference || row.similarRecordReferenceNo || '',
          programType: data?.programType || row.programType || '',
          scheme: data?.scheme || row.scheme || '',
          descriptions,
        },
      })
    } catch (error) {
      setViewModal({
        show: true,
        data: {
          applicationReference: row.similarRecordReferenceNo || '',
          programType: row.programType || '',
          scheme: row.scheme || '',
          descriptions: ['No similar criteria details available.'],
        },
      })
      console.error('Fetch similar record details failed', error)
    }
  }, [])

  const fetchData = async (pageDetails: {
    pageIndex: number
    pageSize: number
  }) => {
    const newFilter = produce(filters, (draft) => {
      draft.page = pageDetails.pageIndex + 1
      draft.limit = pageDetails.pageSize
    })
    setFilters(newFilter)
    await retrieveDataCountAndData(pageDetails)
  }

  const columns = React.useMemo(
    () => [
      ...similarRecordsReportTableColumns,
      {
        Header: 'Actions',
        accessor: 'similarRecordResultId',
        Cell: ({ row }: any) => <ActionsCell row={row} onView={handleView} />,
      },
    ],
    [handleView]
  )

  return (
    <>
      <Row className="page-section-layout m-2 p-2">
        <Col sm={12} className="border-label">
          <div className="border-label-span">Filter Criteria</div>
          <div className="row mt-3 mb-3">
            <Col sm={3}>
              <Form.Group controlId="referenceNo">
                <Form.Label>Reference No</Form.Label>
                <Form.Control
                  type="text"
                  value={filters.referenceNumber}
                  onChange={(e) =>
                    setFilters(
                      produce(filters, (draft) => {
                        draft.referenceNumber = e.target.value
                      })
                    )
                  }
                />
              </Form.Group>
            </Col>
            <Col sm={3}>
              <Form.Group controlId="ompNumber">
                <Form.Label>OMP Number</Form.Label>
                <Form.Control
                  type="text"
                  value={filters.ompNumber}
                  onChange={(e) =>
                    setFilters(
                      produce(filters, (draft) => {
                        draft.ompNumber = e.target.value
                      })
                    )
                  }
                />
              </Form.Group>
            </Col>
            <Col sm={3}>
              <Form.Group controlId="applicantName">
                <Form.Label>Applicant Name</Form.Label>
                <Form.Control
                  type="text"
                  value={filters.applicantName}
                  onChange={(e) =>
                    setFilters(
                      produce(filters, (draft) => {
                        draft.applicantName = e.target.value
                      })
                    )
                  }
                />
              </Form.Group>
            </Col>
            <Col sm={3}>
              <Form.Group controlId="aggrievedPersonName">
                <Form.Label>Aggrieved Person Name</Form.Label>
                <Form.Control
                  type="text"
                  value={filters.aggrievedPersonName}
                  onChange={(e) =>
                    setFilters(
                      produce(filters, (draft) => {
                        draft.aggrievedPersonName = e.target.value
                      })
                    )
                  }
                />
              </Form.Group>
            </Col>
          </div>
          <div className="row mt-3 mb-3">
            <Col sm={6}>
              <Form.Group controlId="createdDateRange">
                <Form.Label>Date</Form.Label>
                <div className="d-flex align-items-center gap-2">
                  <span className="text-nowrap">From</span>
                  <DatePicker
                    className={'form-control w-auto'}
                    selected={
                      filters.createdFrom != null
                        ? new Date(filters.createdFrom * 1000)
                        : null
                    }
                    onSelect={(date: Date) => {
                      const newFilters = produce(filters, (draft) => {
                        draft.createdFrom = date
                          ? new Date(date).setHours(0, 0, 0, 0) / 1000
                          : null
                      })
                      setFilters(newFilters)
                    }}
                    onChange={(date: Date) => {
                      const newFilters = produce(filters, (draft) => {
                        draft.createdFrom = date
                          ? new Date(date).setHours(0, 0, 0, 0) / 1000
                          : null
                      })
                      setFilters(newFilters)
                    }}
                  />
                  <span className="text-nowrap">To</span>
                  <DatePicker
                    className={'form-control w-auto'}
                    selected={
                      filters.createdTo != null
                        ? new Date(filters.createdTo * 1000)
                        : null
                    }
                    onSelect={(date: Date) => {
                      const newFilters = produce(filters, (draft) => {
                        draft.createdTo = date
                          ? new Date(date).setHours(23, 59, 59, 999) / 1000
                          : null
                      })
                      setFilters(newFilters)
                    }}
                    onChange={(date: Date) => {
                      const newFilters = produce(filters, (draft) => {
                        draft.createdTo = date
                          ? new Date(date).setHours(23, 59, 59, 999) / 1000
                          : null
                      })
                      setFilters(newFilters)
                    }}
                  />
                </div>
              </Form.Group>
            </Col>
          </div>
          <div className="d-flex justify-content-right mt-3 mb-3">
            <button className="save-button me-4" onClick={onSubmit}>
              Search
            </button>
            <button className="save-button" onClick={onDownload}>
              Download
            </button>
          </div>
        </Col>
      </Row>

      <Row className="page-section-layout m-2 p-3">
        <div>
          <IterminalTableComponent
            columns={columns}
            data={state}
            fetchData={fetchData}
            loading={loading}
            pageCount={pageCount}
            totalCount={dataCount}
            onClickRow={(row: any) => handleView(row)}
          />
        </div>
      </Row>

      <SimilerReasoningModal
        show={viewModal.show}
        onHide={() => setViewModal((s) => ({ ...s, show: false }))}
        data={viewModal.data}
      />
    </>
  )
}

export default SimilarRecordsReport
